import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Moon, Sun } from 'lucide-react';
import { useTheme } from '@/providers/theme-provider';
import { useToast } from '@/hooks/use-toast';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { fetchUserSettings, getStoredValue, setStoredValue } from '@/lib/utils';
import { supabase } from '@/services';

const updateUserSettings = async (settings: { language: string; timezone: string }) => {
  setStoredValue('language', settings.language);
  setStoredValue('timezone', settings.timezone);
  supabase.auth
    .updateUser({
      data: {
        language: settings.language,
        timezone: settings.timezone,
      },
    })
    .then(({ error }) => {
      if (error) {
        setStoredValue('syncError', '1');
      }
    });

  return settings;
};

export default function Settings() {
  const { toast } = useToast();
  const { theme, setTheme } = useTheme();
  const [currentTheme, setCurrentTheme] = useState(theme);
  const { t, i18n } = useTranslation();

  const { data: userSettings, refetch } = useQuery({
    queryKey: ['userSettings'],
    queryFn: fetchUserSettings,
  });

  useEffect(() => {
    const storedTheme = getStoredValue('theme', 'system');
    setTheme(storedTheme as 'light' | 'dark' | 'system');
    setCurrentTheme(storedTheme as 'light' | 'dark' | 'system');
  }, [setTheme]);

  const updateSettings = useMutation({
    mutationFn: (newSettings: { language: string; timezone: string }) => {
      if (newSettings.language !== userSettings?.language) {
        i18n.changeLanguage(newSettings.language);
      }
      return updateUserSettings(newSettings);
    },
    onSuccess: () => {
      toast({
        title: t('settingsUpdated'),
        description: t('preferencesSaved'),
        duration: 3000,
      });
      refetch();
    },
    onError: () => {
      toast({
        title: t('error'),
        description: t('updateFailed'),
        variant: 'destructive',
        duration: 5000,
      });
    },
  });

  const handleThemeChange = (newTheme: string) => {
    setTheme(newTheme as 'light' | 'dark' | 'system');
    setCurrentTheme(newTheme as 'light' | 'dark' | 'system');
    setStoredValue('theme', newTheme);
  };

  const handleSettingChange = (key: 'language' | 'timezone', value: string) => {
    if (userSettings) {
      updateSettings.mutate({ ...userSettings, [key]: value });
    }
  };

  return (
    <div className="container py-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle className="text-xl font-bold">{t('settings')}</CardTitle>
            <CardDescription>{t('customizeExperience')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label>{t('theme')}</Label>
              <RadioGroup
                value={currentTheme}
                onValueChange={handleThemeChange}
                className="flex space-x-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="light" id="light" />
                  <Label htmlFor="light" className="flex items-center">
                    <Sun className="w-4 h-4 mr-2" />
                    {t('light')}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="dark" id="dark" />
                  <Label htmlFor="dark" className="flex items-center">
                    <Moon className="w-4 h-4 mr-2" />
                    {t('dark')}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="system" id="system" />
                  <Label htmlFor="system">{t('system')}</Label>
                </div>
              </RadioGroup>
            </div>
            <div className="space-y-2">
              <Label>{t('language')}</Label>
              <Select
                value={userSettings?.language}
                onValueChange={(value) => handleSettingChange('language', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('selectLanguage')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">{t('english')}</SelectItem>
                  <SelectItem value="es">{t('spanish')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('timezone')}</Label>
              <Select
                value={userSettings?.timezone}
                onValueChange={(value) => handleSettingChange('timezone', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('selectTimezone')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="America/New_York">{t('easternTime')}</SelectItem>
                  <SelectItem value="America/Chicago">{t('centralTime')}</SelectItem>
                  <SelectItem value="America/Denver">{t('mountainTime')}</SelectItem>
                  <SelectItem value="America/Los_Angeles">{t('pacificTime')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
